import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/accounts/profile/').then(res => {
      setProfile(res.data);
      setFormData(res.data);
    }).finally(() => setLoading(false));
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB.');
      return;
    }
    setFormData({ ...formData, photoFile: file });
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setFormData(profile);
    setPhotoPreview(null);
    setDeletePhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    try {
      const data = new FormData();
      const fields = ['phone_number', 'alt_phone_number', 'address', 'city', 'state',
        'firm_name', 'firm_address', 'specialization', 'bio', 'gender', 'date_of_birth',
        'employment_category'];
      fields.forEach(f => { if (formData[f] !== undefined) data.append(f, formData[f] || ''); });
      ['first_name', 'last_name', 'email'].forEach((field) => {
        data.append(field, formData.user?.[field] || '');
      });
      if (deletePhoto) {
        data.append('photo', '');
      } else if (formData.photoFile) {
        data.append('photo', formData.photoFile);
      }

      const res = await api.patch('/accounts/profile/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(res.data);
      setFormData(res.data);
      setPhotoPreview(null);
      setDeletePhoto(false);
      setEditing(false);
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile.');
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B5D1E] mx-auto mt-20"></div>;

  const displayPhoto = deletePhoto ? null : (photoPreview || profile?.photo);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <div className="flex gap-3">
          {editing && (
            <button onClick={handleCancelEdit}
              className="px-6 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition">
              Cancel
            </button>
          )}
          <button
            onClick={editing ? handleSave : () => setEditing(true)}
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition"
          >
            {editing ? 'Save Changes' : 'Edit Profile'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#062E10] to-[#0B5D1E] px-8 py-8 rounded-t-xl flex items-center space-x-6">
          <div className="relative group">
            {displayPhoto ? (
              <img src={displayPhoto} alt="" className="w-24 h-24 rounded-full border-4 border-white object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-full border-4 border-white bg-white/20 flex items-center justify-center text-3xl text-white font-bold">
                {profile?.user?.first_name?.charAt(0)}
              </div>
            )}
            {editing && (
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity border-4 border-transparent">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              </label>
            )}
            {editing && (photoPreview || (!deletePhoto && profile?.photo)) && (
              <button
                type="button"
                onClick={() => {
                  if (photoPreview) {
                    setPhotoPreview(null);
                    setFormData({ ...formData, photoFile: null });
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  } else {
                    setDeletePhoto(true);
                  }
                }}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 shadow"
                title={photoPreview ? 'Remove new photo' : 'Delete photo'}
              >✕</button>
            )}
          </div>
          <div className="text-white">
            <h2 className="text-xl font-bold">{profile?.user?.first_name} {profile?.user?.last_name}</h2>
            <p className="text-green-200">{profile?.membership_number || 'Pending Assignment'}</p>
            <p className="text-green-200 text-sm capitalize">{profile?.membership_category}</p>
          </div>
        </div>

        {/* Photo upload hint when editing */}
        {editing && (
          <div className="px-8 pt-4">
            <p className="text-xs text-gray-400 italic">Hover over your photo to change it. Images must be under 5 MB.</p>
          </div>
        )}

        {/* Details */}
        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { label: 'First Name', field: 'first_name', type: 'text' },
            { label: 'Last Name', field: 'last_name', type: 'text' },
            { label: 'Email', field: 'email', type: 'email' },
          ].map((item) => (
            <div key={item.field}>
              <label className="block text-sm font-medium text-gray-500 mb-1" htmlFor={`user-${item.field}`}>{item.label}</label>
              {editing ? (
                <input
                  id={`user-${item.field}`}
                  type={item.type}
                  value={formData.user?.[item.field] || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    user: { ...formData.user, [item.field]: e.target.value },
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none"
                />
              ) : (
                <p className="text-gray-900">{profile?.user?.[item.field] || '—'}</p>
              )}
            </div>
          ))}

          {/* Editable fields */}
          {[
            { label: 'Gender', field: 'gender', type: 'select', options: ['', 'male', 'female'] },
            { label: 'Date of Birth', field: 'date_of_birth', type: 'date' },
            { label: 'Phone Number', field: 'phone_number' },
            { label: 'Alt Phone', field: 'alt_phone_number' },
            { label: 'Employment Category', field: 'employment_category', type: 'select', options: [
              { value: '', label: 'Select...' },
              { value: 'private_sector', label: 'Private Sector' },
              { value: 'public_sector', label: 'Public Sector' },
            ] },
            { label: 'City', field: 'city' },
            { label: 'State', field: 'state' },
            { label: 'Firm Name', field: 'firm_name' },
            { label: 'Specialization', field: 'specialization' },
          ].map((item) => (
            <div key={item.field}>
              <label className="block text-sm font-medium text-gray-500 mb-1">{item.label}</label>
              {editing ? (
                item.type === 'select' ? (
                  <select
                    value={formData[item.field] || ''} onChange={(e) => setFormData({ ...formData, [item.field]: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none capitalize"
                  >
                    {item.options.map(o => (
                      typeof o === 'string'
                        ? <option key={o} value={o}>{o || 'Select...'}</option>
                        : <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={item.type || 'text'} value={formData[item.field] || ''}
                    onChange={(e) => setFormData({ ...formData, [item.field]: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none"
                  />
                )
              ) : (
                <p className="text-gray-900 capitalize">{profile?.[item.field] || '—'}</p>
              )}
            </div>
          ))}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-1">Office Address</label>
            {editing ? (
              <textarea value={formData.firm_address || ''} onChange={(e) => setFormData({ ...formData, firm_address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" rows={2} />
            ) : (
              <p className="text-gray-900">{profile?.firm_address || '—'}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
            {editing ? (
              <textarea value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" rows={2} />
            ) : (
              <p className="text-gray-900">{profile?.address || '—'}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-1">Bio</label>
            {editing ? (
              <textarea value={formData.bio || ''} onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" rows={3} />
            ) : (
              <p className="text-gray-900">{profile?.bio || '—'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
